import { useAxios } from '@/hooks/useAxios';
import { FileTtl, useUploadFile } from '@/hooks/useFiles';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useMutation } from '@/utils/api';

/**
 * Progress info shape emitted by the underlying `useUploadFile` hook.
 * Mirrors the `ProgressEventDetails` interface defined in `useFiles.ts`.
 */
export interface ProgressInfo {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Caller-facing request shape for `useCreateFileRepository`. The hook
 * internally maps `s3_key` (the JS field name in this UI) to the backend
 * wire field `source_archive_key` per backend-lead's contract.
 */
export interface CreateFileRepositoryRequest {
  s3_key: string;
  name: string;
}

/**
 * Response shape returned by `POST /repository` for a file-type Repository.
 * Backend returns the full Repository asset; we expose the subset the UI
 * needs (key, name, type) plus pass-through for forward compatibility.
 */
export interface CreateFileRepositoryResponse {
  key: string;
  name: string;
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [extra: string]: any;
}

/**
 * Composite mutation result returned from `useUploadAndRegisterZipRepository`.
 */
export interface UploadAndRegisterResult {
  assetKey: string;
  fileName: string;
}

/**
 * `useCreateFileRepository` — registers a previously-uploaded zip in S3 as
 * a file-type Repository asset via `POST /repository`.
 *
 * Wire-format mapping:
 *   - JS `s3_key` (request)  →  body `source_archive_key`
 *   - body `name`            =  caller-supplied filename
 *
 * The endpoint is purpose-built for creating file-type repositories from a
 * previously-uploaded zip; `type: "file"` and asset status are set
 * server-side, so the client body contains only `name` and
 * `source_archive_key`.
 *
 * On success invalidates `getQueryKey.getMy('repository')` so the new asset
 * surfaces in subsequent search dropdowns.
 */
export function useCreateFileRepository() {
  const axios = useAxios();

  return useMutation<
    CreateFileRepositoryResponse,
    Error,
    CreateFileRepositoryRequest
  >({
    defaultErrorMessage: 'Failed to register zip as repository',
    getErrorFromResponse: true,
    mutationFn: async ({ s3_key, name }) => {
      const { data } = await axios.post('/repository', {
        name,
        source_archive_key: s3_key,
      });
      return data as CreateFileRepositoryResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('repository'),
      });
    },
  });
}

/**
 * Strip characters from a user-supplied filename that have special meaning
 * downstream:
 *   - `/` and `\` would imply a path; flatten to `_`.
 *   - `#` is the asset-key delimiter (`#repository#<url>#<name>`) and would
 *     produce an invalid Repository.Key. tabularium's `Valid()` already
 *     rejects keys with `#` in any segment, but sanitizing client-side
 *     gives the user a usable filename instead of a backend 400.
 *   - Other control bytes (NUL, etc.) get the same treatment.
 *
 * Whitespace and unicode are intentionally preserved — they're fine in
 * an S3 key and useful for display.
 */
function sanitizeFilename(name: string): string {
  // eslint-disable-next-line no-control-regex
  return name.replace(/[/\\#\x00-\x1f\x7f]/g, '_');
}

/**
 * `useUploadAndRegisterZipRepository` — composite mutation that:
 *   1. uploads a zip File to S3 under `capability-inputs/<ts>-<filename>`
 *      via the existing `useUploadFile` primitive (auto-multipart >2GB),
 *      with the caller-supplied TTL (undefined = keep forever) so uploaded
 *      source can auto-expire (CUS-1618) — the uploader UI owns the default,
 *   2. registers the uploaded blob as a file-type Repository asset.
 *
 * If the upload step throws, the register step is NOT called (orphan
 * S3 file is acceptable per Noah's spec).
 *
 * The File is passed straight through to `useUploadFile`; the underlying
 * XHR sends Blob/File natively without buffering bytes into JS heap, so
 * multi-GB archives don't OOM the browser.
 *
 * `mutateAsync` returns `{ assetKey, fileName }` on full success.
 * Callers can pass an AbortSignal to cancel in-flight uploads (e.g.
 * when the user switches modes or closes the drawer).
 */
export function useUploadAndRegisterZipRepository() {
  const upload = useUploadFile();
  const register = useCreateFileRepository();

  return {
    isPending: upload.isPending || register.isPending,
    error: upload.error ?? register.error,
    uploadStatus: upload.status,
    registerStatus: register.status,
    mutateAsync: async (
      file: File,
      onProgress?: (p: ProgressInfo) => void,
      signal?: AbortSignal,
      ttl?: FileTtl
    ): Promise<UploadAndRegisterResult> => {
      const safeName = sanitizeFilename(file.name);
      const s3_key = `capability-inputs/${Date.now()}-${safeName}`;

      await upload.mutateAsync({
        name: s3_key,
        content: file, // Blob/File — streamed by XHR, no heap buffering
        onProgress,
        ignoreSnackbar: true,
        signal,
        ...(ttl && { ttl }),
      });

      if (signal?.aborted) {
        // Don't register an asset for an upload that was cancelled
        // mid-flight; the S3 object is orphaned but the API stays clean.
        throw new DOMException('Aborted', 'AbortError');
      }

      const result = await register.mutateAsync({ s3_key, name: safeName });

      return { assetKey: result.key, fileName: result.name };
    },
  };
}
